import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  IconButton,
  Tooltip,
  Popover,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Typography,
  Box,
  Divider,
  Button,
  Stack
} from '@mui/material';
import { ViewColumn, Visibility, VisibilityOff } from '@mui/icons-material';

const ColumnManager = ({ columns, visibleColumns, onColumnVisibilityChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggleColumn = (columnName) => {
    const newVisibleColumns = visibleColumns.includes(columnName)
      ? visibleColumns.filter(col => col !== columnName)
      : [...visibleColumns, columnName];

    onColumnVisibilityChange(newVisibleColumns);
  };

  const handleShowAll = () => {
    const allColumnNames = columns
      .filter(col => col.name) // Solo columnas con nombre
      .map(col => col.name);
    onColumnVisibilityChange(allColumnNames);
  };

  const handleHideAll = () => {
    onColumnVisibilityChange([]);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'column-manager-popover' : undefined;

  // Filtrar columnas que tienen nombre (pueden ser visibles/ocultas)
  const manageableColumns = columns.filter(col => col.name);

  return (
    <>
      <Tooltip title="Gestionar columnas">
        <IconButton
          onClick={handleClick}
          sx={{
            "&:hover": (theme) => ({
              color: theme.palette.primary.main,
            }),
          }}
        >
          <ViewColumn />
        </IconButton>
      </Tooltip>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            minWidth: 250,
            maxHeight: 400,
            overflow: 'auto'
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Columnas visibles
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleShowAll}
              startIcon={<Visibility />}
            >
              Mostrar todas
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handleHideAll}
              startIcon={<VisibilityOff />}
            >
              Ocultar todas
            </Button>
          </Stack>

          <Divider sx={{ mb: 1 }} />

          <List dense>
            {manageableColumns.map((column) => {
              const isVisible = visibleColumns.includes(column.name);
              return (
                <ListItem
                  key={column.name}
                  button
                  onClick={() => handleToggleColumn(column.name)}
                  sx={{ py: 0.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox
                      edge="start"
                      checked={isVisible}
                      tabIndex={-1}
                      disableRipple
                      size="small"
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={column.label || column.name}
                    primaryTypographyProps={{
                      variant: 'body2',
                      color: isVisible ? 'text.primary' : 'text.secondary'
                    }}
                  />
                </ListItem>
              );
            })}
          </List>

          {manageableColumns.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No hay columnas configurables
            </Typography>
          )}
        </Box>
      </Popover>
    </>
  );
};

ColumnManager.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    label: PropTypes.string,
  })).isRequired,
  visibleColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
  onColumnVisibilityChange: PropTypes.func.isRequired,
};

export default ColumnManager;
